import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit"
    }).toUpperCase();
  } catch {
    return "";
  }
}
function JournalConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        <div className="lg:col-span-5">
          <div className="aspect-[4/5] bg-card animate-pulse" />
          <div className="h-px bg-foreground/20 mt-6" />
          <div className="h-4 bg-card animate-pulse mt-4 w-1/3" />
          <div className="h-8 bg-card animate-pulse mt-3 w-3/4" />
        </div>
        <div className="lg:col-span-7 flex flex-col">
          {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="py-8 border-t border-foreground/15">
              <div className="h-3 bg-card animate-pulse w-1/3" />
              <div className="h-7 bg-card animate-pulse mt-3 w-4/5" />
            </div>)}
        </div>
      </div>;
  }
  if (error || posts.length === 0) {
    return <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
        No wellness guides available yet.
      </p>;
  }
  const lead = posts[0];
  const rest = posts.slice(1, 4);
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
      {}
      <article className="lg:col-span-5 group">
        <Link to={lead.link || "#"} className="block">
          <div className="relative aspect-[4/5] overflow-hidden bg-card">
            {}
            <span aria-hidden className="absolute top-2 left-2 z-10 text-foreground/70 text-[10px] leading-none select-none">+</span>
            <span aria-hidden className="absolute bottom-2 right-2 z-10 text-foreground/70 text-[10px] leading-none select-none">+</span>
            {lead.featuredImage ? <img src={lead.featuredImage} alt={lead.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[600ms] ease-[cubic-bezier(0.7,0,0.2,1)] group-hover:scale-[1.03]" /> : <div className="w-full h-full bg-card" />}
          </div>

          <div className="relative h-px bg-foreground/30 mt-6">
            <span className="absolute -left-0 top-1/2 -translate-y-1/2 w-1 h-1 bg-primary" />
          </div>

          <div className="mt-5 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            <span>{formatDate(lead.date)}</span>
            {lead.categories && lead.categories.length > 0 && <>
                <span aria-hidden className="w-1 h-1 bg-primary inline-block" />
                <span className="text-foreground/80">{lead.categories[0].name}</span>
              </>}
          </div>

          <h3 className="font-default font-normal text-foreground mt-3 leading-[1.12] tracking-[-0.015em]" style={{
          fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)",
          fontWeight: 600
        }}>
            {lead.title}
          </h3>

          {lead.excerpt && <p className="mt-4 text-foreground/75 leading-[1.6] line-clamp-2" style={{
          fontSize: "clamp(0.9375rem, 0.95vw, 1rem)"
        }} dangerouslySetInnerHTML={{
          __html: lead.excerpt
        }} />}
        </Link>
      </article>

      {}
      <div className="lg:col-span-7 flex flex-col">
        {rest.map((post, i) => <article key={post.id} data-index={i} className={`group relative ${i === 0 ? "" : "border-t border-foreground/15"}`}>
            <Link to={post.link || "#"} className="block py-8 pl-6 pr-2 relative">
              {}
              <span aria-hidden className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-1 bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-[360ms] ease-[cubic-bezier(0.7,0,0.2,1)]" />

              <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                <span>{formatDate(post.date)}</span>
                {post.categories && post.categories.length > 0 && <>
                    <span aria-hidden className="w-1 h-1 bg-primary inline-block" />
                    <span className="text-foreground/80">{post.categories[0].name}</span>
                  </>}
              </div>

              <h4 className="font-default font-normal text-foreground mt-3 leading-[1.12] tracking-[-0.015em] relative inline-block" style={{
            fontSize: "clamp(1.25rem, 1.9vw, 1.75rem)",
            fontWeight: 600
          }}>
                <span className="relative inline">
                  {post.title}
                  <span aria-hidden className="absolute left-0 -bottom-1 h-px bg-primary w-0 group-hover:w-full transition-[width] duration-[360ms] ease-[cubic-bezier(0.7,0,0.2,1)]" />
                </span>
              </h4>
            </Link>
          </article>)}
      </div>
    </div>;
}
export default function LatestFromThePetWellnessBlog() {
  const wp_query = new WP_Query({
    post_type: "post",
    per_page: 4,
    orderby: "date",
    order: "desc"
  });
  return <section data-section-id="2096" id="latest-from-the-pet-wellness-blog" className="relative bg-background text-foreground py-28 md:py-36 overflow-hidden border-t border-foreground/20">
      <div className="container mx-auto max-w-[1440px] px-6 md:px-12">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end relative">
          {}
          <span aria-hidden className="pointer-events-none absolute -top-8 -left-2 md:-left-4 font-default select-none leading-none" style={{
          fontSize: "clamp(8rem, 16vw, 16rem)",
          fontWeight: 380,
          color: "transparent",
          WebkitTextStroke: "0.5px hsl(210 10% 23% / 0.08)",
          zIndex: 0
        }}>
            05
          </span>

          <div className="lg:col-span-8 relative z-10">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
              § 05 — PET WELLNESS INSIGHTS
            </p>
            <h2 className="font-default text-foreground leading-[1.02] tracking-[-0.02em]" style={{
            fontSize: "clamp(2.25rem, 4.6vw, 4.5rem)",
            fontWeight: 600
          }}>
              Expert advice for <em className="italic font-normal">healthier</em> pets.
            </h2>
          </div>

          <div className="lg:col-span-4 relative z-10 flex flex-col lg:items-end lg:text-right gap-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              04 Guides · Updated Weekly
            </p>
            <Link to="/ShopPage" className="group inline-flex">
              <Button variant="link" className="h-auto p-0 font-mono text-xs uppercase tracking-[0.14em] text-foreground hover:text-foreground hover:no-underline">
                <span className="relative inline-flex items-center gap-2">
                  <span className="relative">
                    Explore our resources
                    <span aria-hidden className="absolute left-0 -bottom-1 h-px w-full bg-foreground/60 group-hover:bg-primary transition-colors duration-[220ms]" />
                  </span>
                  <ArrowUpRight className="size-3.5 transition-transform duration-[200ms] ease-[cubic-bezier(0.7,0,0.2,1)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </Button>
            </Link>
          </div>
        </div>

        {}
        <div className="relative h-px bg-foreground/20 mt-12 md:mt-16 mb-12 md:mb-16">
          <span aria-hidden className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-1 bg-primary" />
          <span aria-hidden className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-1 bg-primary" />
        </div>

        {}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 4,
        orderby: "date",
        order: "desc"
      }}>
          <JournalConsumer />
        </PostsProvider>
      </div>
    </section>;
}